import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MaterialModule } from '../../../shared/material.module';
import { Country } from '../../../models/country';
import { AuditFeeCategory } from '../../../models/auditFeeCategory';
import { AuditSectionConfiguration, CreateAuditSectionConfigurationRequest } from '../../../models/auditSectionConfiguration';
import { Observable, forkJoin } from 'rxjs';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { LutService } from '../../../services/lut.service';
import { Router } from '@angular/router';
import { Helpers } from '../../../utilities/helpers';
import { MatCardModule } from '@angular/material/card';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';

interface CategoryGroup {
  primaryCategory: number;
  label: string;
  categories: AuditFeeCategory[];
}

@Component({
  selector: 'app-audit-section-configuration',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatChipsModule,
    MatCardModule,
    NgxSpinnerModule
  ],
  templateUrl: './audit-section-configuration.component.html',
  styleUrl: './audit-section-configuration.component.scss'
})

export class AuditSectionConfigurationComponent implements OnInit {
  form!: FormGroup;
  helpers = Helpers;

  private lookupService = inject(LookupDataService);
  private lutService = inject(LutService);
  private toastService = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);

  countries$: Observable<Country[] | null>;
  countriesLoading$: Observable<boolean>;
  public countries: Country[] = [];

  public auditFeeCategories: AuditFeeCategory[] = [];
  public groupedCategories: CategoryGroup[] = [];
  public auditSectionConfigurations: AuditSectionConfiguration[] = [];
  private originalSelectedCategories: string[] = [];

  private categoryLabels: { [key: number]: string } = {
    1: 'Audit',
    2: 'Related',
    3: 'Tax',
    4: 'Other'
  };

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private formBuilder: FormBuilder,
    private router: Router) {
    this.form = this.formBuilder.group({
      country: ['', Validators.required],
      selectedCategories: [[]]
    });
    this.countries$ = this.lookupService.getVisibleCountries();
    this.countriesLoading$ = this.lookupService.getTableLoadingState('countries');
    this.countries$.subscribe(data => {
      if (data) {
        this.countries = data.sort((a, b) => a.name.localeCompare(b.name));
      }
    });

    // Subscribe to country changes to update selected categories
    this.form.get('country')?.valueChanges.subscribe((countryId) => {
      this.updateSelectedCategoriesForCountry(countryId);
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.spinnerService.show();
    forkJoin({
      categories: this.lutService.getAuditFeeCategories(),
      configurations: this.lutService.getAuditSectionConfigurations()
    }).subscribe({
      next: ({ categories, configurations }) => {
        this.auditFeeCategories = categories.sort((a, b) => a.name.localeCompare(b.name));
        this.auditSectionConfigurations = configurations;
        this.groupCategories();
        this.spinnerService.hide();

        // Update selected categories if a country is already selected
        const selectedCountry = this.form.get('country')?.value;
        if (selectedCountry) {
          this.updateSelectedCategoriesForCountry(selectedCountry);
        }
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.spinnerService.hide();
        this.toastService.error('Error loading data');
      }
    });
  }

  updateSelectedCategoriesForCountry(countryId: string): void {
    if (!countryId) {
      this.form.get('selectedCategories')?.setValue([]);
      this.originalSelectedCategories = [];
      return;
    }

    // Find all configurations for the selected country
    const configsForCountry = this.auditSectionConfigurations.filter(
      config => config.countryId === countryId
    );

    // Extract the category IDs that are configured (and thus should be selected as "not visible")
    const selectedCategoryIds = configsForCountry.map(config => config.auditFeeCategoryId);
    this.form.get('selectedCategories')?.setValue(selectedCategoryIds);
    this.originalSelectedCategories = [...selectedCategoryIds];
  }

  groupCategories(): void {
    const groups: { [key: number]: AuditFeeCategory[] } = {};

    this.auditFeeCategories.forEach(category => {
      if (!groups[category.primaryCategory]) {
        groups[category.primaryCategory] = [];
      }
      groups[category.primaryCategory].push(category);
    });

    this.groupedCategories = Object.keys(groups)
      .map(key => Number(key))
      .sort((a, b) => a - b)
      .map(primaryCategory => ({
        primaryCategory,
        label: this.categoryLabels[primaryCategory] || 'Unknown',
        categories: groups[primaryCategory].sort((a, b) => a.name.localeCompare(b.name))
      }));
  }

  isCategorySelected(categoryId: string): boolean {
    const selectedCategories = this.form.get('selectedCategories')?.value || [];
    return selectedCategories.includes(categoryId);
  }

  toggleCategory(categoryId: string): void {
    const selectedCategories = this.form.get('selectedCategories')?.value || [];
    const index = selectedCategories.indexOf(categoryId);

    if (index > -1) {
      selectedCategories.splice(index, 1);
    } else {
      selectedCategories.push(categoryId);
    }

    this.form.get('selectedCategories')?.setValue([...selectedCategories]);
  }

  hasChanges(): boolean {
    const currentCategories = this.form.get('selectedCategories')?.value || [];

    // Check if arrays have the same length
    if (currentCategories.length !== this.originalSelectedCategories.length) {
      return true;
    }

    // Check if all elements in current are in original
    const sortedCurrent = [...currentCategories].sort();
    const sortedOriginal = [...this.originalSelectedCategories].sort();

    return !sortedCurrent.every((val, index) => val === sortedOriginal[index]);
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    const countryId = this.form.get('country')?.value;
    const selectedCategoryIds: string[] = this.form.get('selectedCategories')?.value || [];

    // Find existing configurations for this country
    const existingConfigs = this.auditSectionConfigurations.filter(
      config => config.countryId === countryId
    );
    const existingCategoryIds = existingConfigs.map(config => config.auditFeeCategoryId);

    // Determine which categories need to be added (newly selected)
    const categoriesToAdd = selectedCategoryIds.filter(
      catId => !existingCategoryIds.includes(catId)
    );

    // Determine which categories need to be removed (deselected)
    const categoriesToRemove = existingCategoryIds.filter(
      catId => !selectedCategoryIds.includes(catId)
    );

    // Build the API calls
    const createCalls: Observable<AuditSectionConfiguration>[] = categoriesToAdd.map(categoryId => {
      const request: CreateAuditSectionConfigurationRequest = {
        countryId: countryId,
        auditFeeCategoryId: categoryId
      };
      return this.lutService.createAuditSectionConfiguration(request);
    });

    const deleteCalls: Observable<void>[] = categoriesToRemove.map(categoryId => {
      const configToDelete = existingConfigs.find(c => c.auditFeeCategoryId === categoryId);
      return this.lutService.deleteAuditSectionConfiguration(configToDelete!.id);
    });

    // Execute all API calls
    if (createCalls.length === 0 && deleteCalls.length === 0) {
      this.toastService.info('No changes to save');
      return;
    }

    this.spinnerService.show();
    forkJoin([...createCalls, ...deleteCalls] as Observable<any>[]).subscribe({
      next: () => {
        this.spinnerService.hide();
        this.toastService.success('Configuration saved successfully');
        // Reload the configurations
        this.loadData();
      },
      error: (error) => {
        console.error('Error saving configuration:', error);
        this.spinnerService.hide();
        this.toastService.error('Error saving configuration');
      }
    });
  }

  selectAllCountries() {
    const allCountryIds = this.countries.map(c => c.id);
    this.form.get('selectedCountries')?.setValue(allCountryIds);
  }

  deselectAllCountries() {
    this.form.get('selectedCountries')?.setValue([]);
  }

  getSelectedCountriesArray(): any[] {
    const value = this.form.get('selectedCountries')?.value;
    return Array.isArray(value) ? value : [];
  }
}
